import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { setLoggerContext } from '@/lib/logger';

export function useAnalytics() {
  const posthog = usePostHog();
  const { isLoaded, isSignedIn, user } = useUser();
  const { isLoaded: isOrgLoaded, organization } = useOrganization();

  useEffect(() => {
    const transformUserObject = () => {
      const {
        firstName,
        fullName,
        lastName,
        emailAddresses,
        createdAt,
        imageUrl,
        hasImage,
        id,
        lastSignInAt,
        organizationMemberships,
        phoneNumbers,
        primaryEmailAddress,
        primaryPhoneNumber,
        publicMetadata,
        unsafeMetadata,
        updatedAt,
        username,
      } = user;

      return {
        firstName,
        fullName,
        lastName,
        email: primaryEmailAddress?.emailAddress,
        emailAddresses,
        createdAt,
        imageUrl,
        hasImage,
        id,
        lastSignInAt,
        organizationMemberships,
        phoneNumbers,
        primaryEmailAddress,
        primaryPhoneNumber,
        publicMetadata,
        unsafeMetadata,
        updatedAt,
        username,
      };
    };

    if (!posthog || !isLoaded || !isOrgLoaded || !isSignedIn || !user) return;
    const transformedUserObject = transformUserObject();
    const uniqueIdentity = user.primaryEmailAddress?.emailAddress || user.fullName || '';

    // Identify user in PostHog
    posthog?.identify(uniqueIdentity, transformedUserObject);

    // Set global logger context for error events
    setLoggerContext({
      organizationId: organization?.id,
      organizationName: organization?.name,
      email: user.primaryEmailAddress?.emailAddress,
      username: user.username,
    });
  }, [posthog, isLoaded, isOrgLoaded, isSignedIn, user, organization]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trackEvent = (eventName: string, properties: Record<string, any>) => {
    posthog?.capture(eventName, properties);
  };

  const reset = () => {
    posthog?.reset();
  };

  return { trackEvent, reset };
}